import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { ALERT_COLORS, alertVisualStorageKey, DEFAULT_ALERT_VISUAL_SETTINGS, getAlertAccent, normalizeAlertVisualSettings } from "../components/alerts/alertVisualPreferences";
import { createCameraAlertTracker, expireCameraAlerts } from "../components/alerts/cameraAlertTracker";
import { playAlertSound } from "../components/alerts/alertSound";
import saasApi from "../utils/saasApi";

const noop = () => {};
const flashFade = keyframes`from { opacity: 1; } to { opacity: 0; }`;
const AlertVisualContext = createContext({ settings: DEFAULT_ALERT_VISUAL_SETTINGS, activeAlerts: {}, recentAlerts: [], unseenCount: 0, ingestAlerts: noop, clearAlert: noop, updateSettings: noop, persistenceError: false });

export const AlertVisualProvider = ({ children, storageKey = alertVisualStorageKey() }) => {
  const [settings, setSettings] = useState(() => {
    try { return normalizeAlertVisualSettings(JSON.parse(localStorage.getItem(storageKey) || "null")); }
    catch { return normalizeAlertVisualSettings(null); }
  });
  const [persistenceError, setPersistenceError] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState({});
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [flash, setFlash] = useState(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const audioUnlockedRef = useRef(false);
  const trackerRef = useRef(null);
  if (!trackerRef.current) {
    let seen = [];
    try { seen = JSON.parse(sessionStorage.getItem(`${storageKey}:seen`) || "[]"); } catch { /* Use in-memory deduplication. */ }
    trackerRef.current = createCameraAlertTracker(seen, entries => {
      try { sessionStorage.setItem(`${storageKey}:seen`, JSON.stringify(entries)); } catch { /* Visual expiry still works without persistence. */ }
    });
  }
  const ingestAlerts = useCallback((alerts, source = "other") => {
    const fresh = trackerRef.current(Array.isArray(alerts) ? alerts : []);
    const events = Object.values(fresh);
    if (!events.length) return;
    const currentSettings = settingsRef.current;
    const high = currentSettings.enabled && currentSettings.intensity === "high";
    setActiveAlerts(current => ({ ...current, ...Object.fromEntries(Object.entries(fresh).map(([key, event]) => [key, {
      ...event,
      persistent: event.alert.status === "nueva" && Boolean(event.alert.id),
      source,
      pulseUntil: high ? event.receivedAt + 800 : 0,
    }])) }));
    if (high) {
      const priority = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
      const selected = [...events].sort((a, b) => (priority[String(b.alert.severity).toUpperCase()] || 3) - (priority[String(a.alert.severity).toUpperCase()] || 3))[0].alert;
      const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      if (!reducedMotion) setFlash({ key: `${selected.id || selected.occurred_at}:${Date.now()}`, color: getAlertAccent(selected.detection_type, "dark", currentSettings) });
      if (!currentSettings.muted && audioUnlockedRef.current) playAlertSound(selected.severity, currentSettings.volume);
    }
  }, []);
  const clearAlert = useCallback(id => {
    setActiveAlerts(current => Object.fromEntries(Object.entries(current).filter(([, event]) => String(event.alert.id) !== String(id))));
    setRecentAlerts(current => current.filter(alert => String(alert.id) !== String(id)));
  }, []);
  const updateSettings = useCallback(next => {
    const normalized = normalizeAlertVisualSettings(next);
    setSettings(normalized);
    try { localStorage.setItem(storageKey, JSON.stringify(normalized)); setPersistenceError(false); }
    catch { setPersistenceError(true); }
  }, [storageKey]);

  useEffect(() => {
    const onStorage = event => {
      if (event.key !== storageKey) return;
      try { setSettings(normalizeAlertVisualSettings(JSON.parse(event.newValue || "null"))); }
      catch { /* Ignore invalid values from another tab. */ }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey]);

  useEffect(() => {
    const unlock = () => { audioUnlockedRef.current = true; };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => { window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
  }, []);

  useEffect(() => {
    if (!flash) return undefined;
    const timer = setTimeout(() => setFlash(null), 200);
    return () => clearTimeout(timer);
  }, [flash]);

  useEffect(() => {
    const now = Date.now();
    const next = expireCameraAlerts(activeAlerts, settings.durationSeconds, now);
    if (Object.keys(next).length !== Object.keys(activeAlerts).length) { setActiveAlerts(next); return; }
    const finishedPulse = Object.values(next).some(event => event.pulseUntil && event.pulseUntil <= now);
    if (finishedPulse) { setActiveAlerts(current => Object.fromEntries(Object.entries(current).map(([key, event]) => [key, event.pulseUntil && event.pulseUntil <= now ? { ...event, pulseUntil: 0 } : event]))); return; }
    const deadlines = Object.values(next).flatMap(event => [
      ...(!event.persistent ? [event.receivedAt + settings.durationSeconds * 1000] : []),
      ...(event.pulseUntil ? [event.pulseUntil] : []),
    ]);
    if (!deadlines.length) return;
    const timer = setTimeout(() => setActiveAlerts(current => ({ ...current })), Math.max(0, Math.min(...deadlines) - now));
    return () => clearTimeout(timer);
  }, [activeAlerts, settings.durationSeconds]);

  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) setActiveAlerts(current => expireCameraAlerts(current, settings.durationSeconds));
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [settings.durationSeconds]);

  // One tenant-scoped feed supplies the banner, borders and notification count.
  useEffect(() => {
    let active = true;
    let pending = false;
    let retryAfter = 0;
    const poll = async () => {
      const token = localStorage.getItem("token");
      if (!token || localStorage.getItem("role") === "vigilante" || (localStorage.getItem("role") === "superadmin" && !sessionStorage.getItem("superadmin_token")) || pending || Date.now() < retryAfter) return;
      pending = true;
      try {
        const data = await saasApi.getRealtimeAlerts();
        if (active && Array.isArray(data.alerts)) {
          setRecentAlerts(data.alerts);
          setUnseenCount(Number(data.unseen_count) || 0);
          ingestAlerts(data.alerts.filter(alert => alert.status === "nueva" && !alert.viewed_by_me_at), "saas");
          setActiveAlerts(current => {
            const next = { ...current };
            const live = new Map(data.alerts.map(alert => [String(alert.id), alert]));
            Object.keys(next).forEach(key => {
              const event = next[key];
              if (live.has(String(event.alert.id)) && live.get(String(event.alert.id)).status !== "nueva") delete next[key];
              else if (event.source === "saas" && !live.has(String(event.alert.id))) delete next[key];
              else if (live.has(String(event.alert.id))) next[key] = { ...event, source: "saas" };
            });
            data.alerts.filter(alert => alert.status === "nueva" && alert.camera_name).forEach(alert => {
              const key = String(alert.camera_name).trim().replace(/ /g, "_");
              if (!next[key]) next[key] = { alert, receivedAt: Date.now(), persistent: true, source: "saas", pulseUntil: 0 };
            });
            return next;
          });
        }
      } catch { retryAfter = Date.now() + 60000; }
      finally { pending = false; }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => { active = false; clearInterval(interval); };
  }, [ingestAlerts]);

  const value = useMemo(() => ({ settings, updateSettings, activeAlerts, recentAlerts, unseenCount, ingestAlerts, clearAlert, persistenceError }), [settings, updateSettings, activeAlerts, recentAlerts, unseenCount, ingestAlerts, clearAlert, persistenceError]);
  return <AlertVisualContext.Provider value={value}>
    {children}
    {flash && settings.enabled && settings.intensity === "high" && <Box aria-hidden="true" data-alert-flash={flash.key} sx={{ position: "fixed", inset: 0, zIndex: 2000, pointerEvents: "none", bgcolor: alpha(flash.color, 0.2), animation: `${flashFade} 200ms ease-out forwards` }} />}
  </AlertVisualContext.Provider>;
};

export const useAlertVisuals = () => useContext(AlertVisualContext);
export const useAlertAccent = type => {
  const { settings } = useAlertVisuals();
  return mode => getAlertAccent(type, mode, settings);
};
export { ALERT_COLORS };
